import { dialogService, toastService } from '@thefarmersfront/kitchen';

import { useShallow } from 'zustand/react/shallow';

import { ExcelUploadModal } from '~/components/common';

import { usePostRequestGoodsUploadMutation } from '~/hooks/receive';
import { useReceiveStore } from '~/store';

import { RECEIVE, TOAST_MESSAGE } from '~/constants';

import type { TypePostRequestGoodsUploadParams, TypePostRequestGoodsUploadResponse } from '~/types/receive.types';

const infoTexts = ['상품관리에 등록된 상품만 추가할 수 있습니다.', '한 번에 최대 200개 상품까지만 추가할 수 있습니다.'];

type TypeReceiveRequestExcelUploadModalProps = {
  ownerCode: string;
  center: string;
  temperature: string;
  supplierCode: string;
  open: boolean;
  onCancel: () => void;
  onConfirm: (data: TypePostRequestGoodsUploadResponse) => void;
};

/**
 * [입고관리 > 입고요청관리] 입고요청등록모달 > 입고 상품 엑셀 추가 모달
 */
const ReceiveRequestExcelUploadModal = ({
  ownerCode,
  center,
  temperature,
  supplierCode,
  open,
  onCancel,
  onConfirm,
}: TypeReceiveRequestExcelUploadModalProps) => {
  const uploadMutation = usePostRequestGoodsUploadMutation();

  const requestModalGoodsList = useReceiveStore(useShallow((state) => state.requestModalGoodsList));

  const registeredSkuCodes = Array.from(new Set(requestModalGoodsList.map(({ goodsCode }) => goodsCode)));
  const totalSkuCount = registeredSkuCodes.length;

  const handleConfirm = (file: File) => {
    const params: TypePostRequestGoodsUploadParams = {
      multipartFile: file,
      registeredSkuCount: totalSkuCount,
      registeredUnitSum: requestModalGoodsList.length,
      registeredSkuCodes: registeredSkuCodes.join(','),
      cluster: center,
      warehouse: temperature,
      supplierCode: supplierCode,
    };

    uploadMutation.mutate(
      { ownerCode, params },
      {
        onSuccess: (data) => {
          onConfirm(data);
        },
        onError: (error) => {
          if (error?.code === RECEIVE.ROW_COUNT_EXCEEDED) {
            dialogService.addDialog({
              children: '최대 200개의 행까지만 등록 가능합니다. 확인 후 다시 시도해주세요.',
            });
          }

          if (error?.code === RECEIVE.SKU_AND_QUANTITY_COUNT_EXCEEDED) {
            dialogService.addDialog({
              children: '최대 200종의 품목, 20,000개의 수량까지만 등록 가능합니다. 확인 후 다시 시도해주세요.',
            });
          }

          toastService.addToast({
            type: 'error',
            message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR,
          });
        },
      }
    );
  };

  return (
    <ExcelUploadModal
      title="입고 상품 엑셀 추가"
      confirmText="추가"
      accept=".xls,.xlsx"
      depth={2}
      infoTexts={infoTexts}
      open={open}
      onCancel={onCancel}
      onConfirm={handleConfirm}
    />
  );
};

export default ReceiveRequestExcelUploadModal;
